import { useRef, useCallback } from 'react';
import { APP_CONFIG } from '../../config/appConfig';

const BASE_URL = APP_CONFIG.API_BASE_URL;

// Tap sequence that opens the local display-editing prompt: three short taps,
// then long, short, long. Deliberately awkward so a normal user can't hit it by
// accident - it is not hidden to deceive anyone, just to avoid stray edits.
const UNLOCK_PATTERN = ['short', 'short', 'short', 'long', 'short', 'long'];
const LONG_PRESS_MS = 450; // press held longer than this counts as "long"
const SEQUENCE_RESET_MS = 1500; // gap between taps that resets progress

// Detects the unlock tap sequence on an element. Spread the returned handlers
// onto the target; onComplete fires once the full pattern is entered.
export const useSecretGesture = (onComplete, enabled = true) => {
  const pressStart = useRef(0);
  const sequence = useRef([]);
  const resetTimer = useRef(null);

  const resetSequence = useCallback(() => {
    sequence.current = [];
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
      resetTimer.current = null;
    }
  }, []);

  const handlePressStart = () => {
    if (!enabled) return;
    pressStart.current = Date.now();
  };

  const handlePressEnd = () => {
    if (!enabled || !pressStart.current) return;
    const heldMs = Date.now() - pressStart.current;
    pressStart.current = 0;

    const tap = heldMs >= LONG_PRESS_MS ? 'long' : 'short';
    sequence.current.push(tap);

    // Keep only what still matches the start of the pattern
    const idx = sequence.current.length - 1;
    if (sequence.current[idx] !== UNLOCK_PATTERN[idx]) {
      sequence.current = tap === UNLOCK_PATTERN[0] ? [tap] : [];
    }

    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(resetSequence, SEQUENCE_RESET_MS);

    if (sequence.current.length === UNLOCK_PATTERN.length) {
      resetSequence();
      onComplete();
    }
  };

  return {
    // preventDefault stops the browser selecting the underlying text on
    // rapid taps, which otherwise swallows the press events.
    onMouseDown: (e) => { e.preventDefault(); handlePressStart(); },
    onMouseUp: handlePressEnd,
    onMouseLeave: () => { pressStart.current = 0; },
    onTouchStart: handlePressStart,
    onTouchEnd: (e) => { e.preventDefault(); handlePressEnd(); },
    style: { userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', cursor: 'default' },
  };
};

// Verifies the passphrase against the backend gate. Resolves true/false.
export const verifyAdminPassphrase = async (passphrase) => {
  const res = await fetch(`${BASE_URL}/system/health-check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ passphrase }),
  });
  return res.ok;
};
